// eslint-disable-next-line filenames/match-regex
import {useMemo} from 'react'

import type {ItemPosition} from '../contexts/IssueNavigationContext'
import {issueUrl, pullRequestUrl} from '../utils/urls'

type NavigationType = {
  __id: string
  number: number
  repository: {
    name: string
    owner: {
      login: string
    }
  }
}

type IssueNavigationType = NavigationType & {
  __typename: 'Issue'
}

type PRNavigationType = NavigationType & {
  __typename: 'PullRequest'
}

const setNavigationUrl = (type: string, owner: string, repo: string, number: number) => {
  if (type === 'Issue') {
    return issueUrl({owner, repo, number})
  } else if (type === 'PullRequest') {
    return pullRequestUrl({owner, repo, number})
  }
}

export const useNavDataNeighbours = (data: Array<IssueNavigationType | PRNavigationType | null>) => {
  return useMemo(
    function getNeighbours() {
      return data.reduce(
        (acc, thread, index) => {
          if (!thread) return {}
          if (data[index]?.__typename !== 'Issue') return acc

          let nextIndex = index + 1
          while (data[nextIndex]?.__typename !== 'Issue' && nextIndex < data.length) {
            nextIndex++
          }

          let prevIndex = index - 1
          while (data[prevIndex]?.__typename !== 'Issue' && prevIndex >= 0) {
            prevIndex--
          }

          const isFirst: boolean = index <= 0
          const isLast = nextIndex >= data.length

          const nextItem = data[nextIndex]?.__typename === 'Issue' ? data[nextIndex] : null
          const prevItem = data[prevIndex]?.__typename === 'Issue' ? data[prevIndex] : null

          const nextThread =
            !isLast && nextItem
              ? {
                  id: nextItem.__id,
                  url: setNavigationUrl(
                    nextItem.__typename,
                    nextItem.repository.owner.login,
                    nextItem.repository.name,
                    nextItem.number,
                  ),
                }
              : null
          const prevThread =
            !isFirst && prevItem
              ? {
                  id: prevItem?.__id,
                  url: setNavigationUrl(
                    prevItem.__typename,
                    prevItem.repository.owner.login,
                    prevItem.repository.name,
                    prevItem.number,
                  ),
                }
              : null
          const result = {
            ...acc,
            [thread.__id]: {
              isFirst,
              isLast,
              next: nextThread,
              previous: prevThread,
            },
          }
          return result
        },
        {} as Record<string, ItemPosition>,
      )
    },
    [data],
  )
}
